import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { ReportUserPayload } from 'src/models/report/ReportPayload';
import { RootState } from '.';
import api from '../api/BlancoClient';
import { extractInvalidChars } from '../lib/message-validator';
import { DreamHistory } from '../models/dream/DreamHistory';
import { DreamLand } from '../models/dream/DreamLand';
import { ReportError } from '../models/report/ReportError';
import { UserProfile } from '../models/UserProfile';
import { actions as userActions, getFreshToken, UserState } from './user';
import { checkErrorType } from './utils/helper';
import { RequestState } from './utils/RequestState';

export type DreamErrorType = 'loading-failed' | 'unstable-network' | 'unknown';

type DreamDetailRequestState = RequestState<{
  dreamLand?: DreamLand;
  histories: DreamHistory[];
}>;

export interface DreamState {
  history: DreamDetailRequestState;
  historyDetail: {
    userProfile?: RequestState<UserProfile, DreamErrorType>;
    reportStatus?: RequestState<Record<string, unknown>, ReportError>;
    deleteState?: RequestState<DreamHistory, DreamErrorType>;
  };
}

const initialState: DreamState = {
  history: { state: 'pending' },
  historyDetail: {}
};

const slice = createSlice({
  name: 'dream',
  initialState,
  reducers: {
    getLandHistories: state => {
      state.history = { state: 'pending' };
    },

    getLandHistoriesSucceeded: (
      state,
      { payload }: PayloadAction<{ dreamLand: DreamLand; histories: DreamHistory[] }>
    ) => {
      state.history = {
        state: 'ok',
        result: payload
      };
    },

    getLandHistoriesFailed: state => {
      state.history = { state: 'error', error: 'load-error' };
    },

    getUserProfile: (state, _: PayloadAction<{ userId: string; language: string }>) => {
      state.historyDetail.userProfile = { state: 'pending' };
    },

    getUserProfileSucceeded: (state, { payload }: PayloadAction<UserProfile>) => {
      state.historyDetail.userProfile = {
        state: 'ok',
        result: payload
      };
    },

    getUserProfileFailed: (state, { payload }: PayloadAction<DreamErrorType>) => {
      state.historyDetail.userProfile = {
        state: 'error',
        error: payload
      };
    },

    deleteLandHistory: (state, _: PayloadAction<{ address: string }>) => {
      state.historyDetail.deleteState = { state: 'pending' };
    },

    deleteLandHistorySucceeded: (state, { payload }: PayloadAction<DreamHistory>) => {
      state.historyDetail.deleteState = { state: 'ok', result: payload };
      if (state.history.state === 'ok') {
        const { result } = state.history;
        result.histories = result.histories.filter(
          item => item.dreamLand.address !== payload.dreamLand.address
        );
      }
    },

    deleteLandHistoryFailed: (state, { payload }: PayloadAction<DreamErrorType>) => {
      state.historyDetail.deleteState = { state: 'error', error: payload };
    },

    resetDeleteLandHistory: state => {
      state.historyDetail.deleteState = undefined;
    },

    reportUser: (state, _: PayloadAction<ReportUserPayload>) => {
      state.historyDetail.reportStatus = { state: 'pending' };
    },

    reportUserSucceeded: state => {
      state.historyDetail.reportStatus = { state: 'ok', result: {} };
      state.historyDetail.userProfile = undefined;
    },

    reportUserFailed: (state, { payload }: PayloadAction<ReportError>) => {
      state.historyDetail.reportStatus = { state: 'error', error: payload };
    },

    resetReportUser: state => {
      state.historyDetail.reportStatus = undefined;
    }
  }
});

export default slice.reducer;

const {
  getLandHistories,
  getUserProfile,
  deleteLandHistory,
  resetDeleteLandHistory,
  reportUser,
  resetReportUser,
  ...internalActions
} = slice.actions;

export const actions = {
  getLandHistories,
  getUserProfile,
  deleteLandHistory,
  resetDeleteLandHistory,
  reportUser,
  resetReportUser
};

const {
  getLandHistoriesSucceeded,
  getLandHistoriesFailed,
  getUserProfileSucceeded,
  getUserProfileFailed,
  deleteLandHistorySucceeded,
  deleteLandHistoryFailed,
  reportUserSucceeded,
  reportUserFailed
} = internalActions;

// 夢をアップロードしていないユーザはエラーになるが，undefinedで復帰
const requestDreamLand = async (userId: string, baid: string) => {
  try {
    return await api.getDreamLand(userId, baid);
  } catch (error) {
    if (error.response.data?.code === '3005') {
      return undefined;
    } else {
      throw error;
    }
  }
};

function* getLandHistoriesSaga() {
  const user: UserState = yield select((state: RootState) => state.user);
  const { selectedUserId, baid } = user;
  if (!selectedUserId || !baid) {
    // 復帰不可能なエラー
    yield put(getLandHistoriesFailed());
    return;
  }
  try {
    const [dreamLand, landHistory]: [DreamLand, { histories: DreamHistory[] }] = yield all([
      call(requestDreamLand, selectedUserId, baid),
      call(api.getDreamLandHistory, selectedUserId, baid)
    ]);
    const { histories } = landHistory;
    const sortedHistories = histories.sort((a, b) => b.visitedAt - a.visitedAt);
    const result = { dreamLand, histories: sortedHistories };
    yield put(getLandHistoriesSucceeded(result));
  } catch (error) {
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else {
      yield put(getLandHistoriesFailed());
    }
  }
}

function* getUserProfileSaga(action: {
  type: typeof getUserProfile.type;
  payload: { userId: string; language: string };
}) {
  const { userId, language } = action.payload;
  try {
    const baid: string = yield call(getFreshToken);
    const profile: UserProfile = yield call(api.getUserProfile, userId, language, baid);
    yield put(getUserProfileSucceeded(profile));
  } catch (error) {
    const errorType = checkErrorType(error);
    switch (errorType) {
      case 'server':
        yield put(userActions.causeServerError(error.response.data?.code));
        break;
      case 'unstable-network':
        yield put(getUserProfileFailed('unstable-network'));
        break;
      default:
        if (error.response.data?.code === '1101') {
          // 通報NGのユーザはプロフィールの取得が失敗してもエラーとしない。
        } else {
          yield put(getUserProfileFailed('loading-failed'));
        }
    }
  }
}

function* deleteLandHistorySaga(action: {
  type: typeof deleteLandHistory.type;
  payload: { address: string };
}) {
  const user: UserState = yield select((state: RootState) => state.user);
  const dreamState: DreamState = yield select((state: RootState) => state.dream);
  const { selectedUserId } = user;
  if (!selectedUserId || dreamState.history.state !== 'ok') {
    // 復帰不可能なエラー
    yield put(deleteLandHistoryFailed('unknown'));
    return;
  }

  const { address } = action.payload;
  try {
    const baid: string = yield call(getFreshToken);
    yield call(api.deleteDreamLandHistory, selectedUserId, address, baid);

    const deleted = dreamState.history.result.histories.find(
      item => item.dreamLand.address === address
    );
    if (deleted) {
      yield put(deleteLandHistorySucceeded(deleted));
    } else {
      yield put(deleteLandHistoryFailed('unknown'));
    }
  } catch (error) {
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(deleteLandHistoryFailed('unstable-network'));
    } else {
      yield put(deleteLandHistoryFailed('loading-failed'));
    }
  }
}

function* reportUserSaga(action: { type: typeof reportUser.type; payload: ReportUserPayload }) {
  const reasonText = action.payload.reason;
  if (reasonText) {
    const invalidChars = extractInvalidChars(reasonText);
    if (invalidChars) {
      yield put(reportUserFailed({ type: 'invalid-char', char: invalidChars }));
      return;
    }
  }
  try {
    const baid: string = yield call(getFreshToken);
    const { type, language, category, reason, ugc } = action.payload;
    const { contentId, digest, createdAt } = ugc;
    yield call(
      api.reportSpam,
      type,
      category,
      reason,
      language,
      contentId,
      digest,
      createdAt,
      baid
    );
    yield put(reportUserSucceeded());
  } catch (error) {
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(reportUserFailed({ type: 'unstable-network' }));
    } else {
      yield put(reportUserFailed({ type: 'unknown' }));
    }
  }
}

export function* dreamSaga() {
  yield takeLatest(getLandHistories.type, getLandHistoriesSaga);
  yield takeLatest(getUserProfile.type, getUserProfileSaga);
  yield takeLatest(reportUser.type, reportUserSaga);
  yield takeLatest(deleteLandHistory.type, deleteLandHistorySaga);
}
